import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { User, ShoppingCart } from "lucide-react";
import { useLang } from "@/context/LangContext";

const IKAS_URL = "https://paklashop.myikas.com";
const IKAS_ACCOUNT_URL = "https://paklashop.myikas.com/account";
const IKAS_CART_URL = "https://paklashop.myikas.com/cart";

const Navbar = () => {
  const { lang, setLang, t, langs } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const items = [
    { key: "product", href: "#product" },
    { key: "lab", href: "#lab" },
    { key: "howto", href: "#howto" },
    { key: "faq", href: "#faq" },
    { key: "contact", href: "#contact" },
  ];

  return (
    <motion.header
      initial={{ y: -32, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-[#FAF7F0]/85 border-b border-[#E5DFCE]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16">
        <div className="flex items-center justify-between h-24 md:h-28">
          <a href="#top" data-testid="nav-logo" className="flex items-center group">
            <img
              src="/logos/pakla_logo_clean.png"
              alt="PAKLA"
              className="h-20 md:h-24 w-auto object-contain"
              draggable="false"
            />
          </a>

          <nav className="hidden lg:flex items-center gap-10">
            {items.map((it) => (
              <a
                key={it.key}
                href={it.href}
                data-testid={`nav-${it.key}`}
                className="relative text-[15px] font-semibold tracking-[0.08em] uppercase text-[#0F3D2E] hover:text-[#2E8B4A] transition-colors"
              >
                <span className="pb-1 border-b border-transparent hover:border-[#2E8B4A]">
                  {t.nav[it.key]}
                </span>
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-2 md:gap-4">
            {/* Language switcher */}
            <div className="relative">
              <button
                type="button"
                data-testid="lang-switch-button"
                onClick={() => setOpen((v) => !v)}
                className="flex items-center gap-2 px-3 h-10 rounded-full border border-[#0F3D2E]/25 text-[12px] tracking-[0.2em] uppercase text-[#0F3D2E] hover:bg-[#0F3D2E] hover:text-[#FAF7F0] transition-colors"
                aria-haspopup="listbox"
                aria-expanded={open}
              >
                <span className="w-1.5 h-1.5 rounded-full bg-[#2E8B4A]" />
                {lang.toUpperCase()}
                <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                  <path d="M1 3l4 4 4-4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
                </svg>
              </button>
              {open && (
                <motion.ul
                  initial={{ opacity: 0, y: -6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2 }}
                  className="absolute right-0 mt-2 min-w-[120px] bg-[#FAF7F0] border border-[#E5DFCE] rounded-xl shadow-lg overflow-hidden"
                  role="listbox"
                  data-testid="lang-menu"
                >
                  {langs.map((l) => (
                    <li key={l.code}>
                      <button
                        type="button"
                        data-testid={`lang-option-${l.code}`}
                        onClick={() => {
                          setLang(l.code);
                          setOpen(false);
                        }}
                        className={`w-full text-left px-4 py-2.5 text-[12px] tracking-[0.2em] uppercase transition-colors ${
                          lang === l.code
                            ? "bg-[#0F3D2E] text-[#FAF7F0]"
                            : "text-[#0F3D2E] hover:bg-[#F2EEE3]"
                        }`}
                      >
                        {l.label}
                      </button>
                    </li>
                  ))}
                </motion.ul>
              )}
            </div>

            {/* User icon → ikas account */}
            <a
              href={IKAS_ACCOUNT_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-account"
              aria-label="Hesabım / Giriş Yap"
              className="inline-flex items-center justify-center w-10 h-10 rounded-full border border-[#0F3D2E]/25 text-[#0F3D2E] hover:bg-[#0F3D2E] hover:text-[#FAF7F0] transition-colors"
            >
              <User size={18} strokeWidth={1.7} />
            </a>

            {/* Cart icon → ikas cart */}
            <a
              href={IKAS_CART_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-cart"
              aria-label="Sepet"
              className="inline-flex items-center justify-center w-10 h-10 rounded-full border border-[#0F3D2E]/25 text-[#0F3D2E] hover:bg-[#0F3D2E] hover:text-[#FAF7F0] transition-colors"
            >
              <ShoppingCart size={18} strokeWidth={1.7} />
            </a>

            <a
              href={IKAS_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-cta"
              className="hidden md:inline-flex items-center gap-2 h-11 px-6 rounded-full bg-[#7BC144] text-white text-[13px] font-bold tracking-[0.08em] hover:bg-[#68A93A] transition-colors shadow-md"
            >
              Hemen Satın Al
              <svg width="14" height="14" viewBox="0 0 12 12" fill="none">
                <path d="M2 6h8m0 0L7 3m3 3L7 9" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </a>

            {/* Mobile menu */}
            <button
              type="button"
              onClick={() => setMenuOpen((v) => !v)}
              data-testid="mobile-menu-toggle"
              className="lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-full border border-[#0F3D2E]/25 text-[#0F3D2E]"
              aria-label="Menu"
            >
              <span className="relative w-4 h-3">
                <span className={`absolute left-0 right-0 top-0 h-[1.5px] bg-current transition-transform ${menuOpen ? "translate-y-1 rotate-45" : ""}`} />
                <span className={`absolute left-0 right-0 bottom-0 h-[1.5px] bg-current transition-transform ${menuOpen ? "-translate-y-[6px] -rotate-45" : ""}`} />
              </span>
            </button>
          </div>
        </div>

        {menuOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            className="lg:hidden overflow-hidden border-t border-[#E5DFCE]"
            data-testid="mobile-menu"
          >
            <ul className="py-4">
              {items.map((it) => (
                <li key={it.key}>
                  <a
                    href={it.href}
                    onClick={() => setMenuOpen(false)}
                    data-testid={`mobile-nav-${it.key}`}
                    className="block px-2 py-3 text-[13px] tracking-[0.16em] uppercase text-[#0F3D2E]"
                  >
                    {t.nav[it.key]}
                  </a>
                </li>
              ))}
            </ul>
          </motion.div>
        )}
      </div>
    </motion.header>
  );
};

export default Navbar;
